import { useNavigate } from 'react-router-dom'
import { ChevronRight, CreditCard } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { usePerks } from '@/lib/perksApi'
import { daysBetween, linkSuggestions, money, openItems, plural, quarterName, quarterOf, unusedThisQuarter } from '@/lib/perks'
import { BenefitRow } from './PerkVisuals'

/**
 * Dashboard summary: unused credits this quarter and the next few to expire.
 * People with linked cards but no perks set up get a one-line invitation;
 * everyone else sees nothing.
 */
export function PerksDashboardCard() {
  const navigate = useNavigate()
  const { data: state } = usePerks()
  if (!state) return null
  const go = () => navigate('/perks')

  if (state.cards.length === 0) {
    if (state.credit_accounts.length === 0) return null
    return (
      <Card>
        <CardContent className="flex items-center gap-4 p-5">
          <CreditCard className="h-5 w-5 text-primary-500" />
          <div className="flex-1 text-sm text-gray-600 dark:text-gray-400">
            Track the credits on your {state.credit_accounts.length === 1 ? 'card' : plural(state.credit_accounts.length, 'card')} and see what each one pays back.
          </div>
          <Button variant="outline" size="sm" onClick={go}>Set up Card Perks</Button>
        </CardContent>
      </Card>
    )
  }

  const today = new Date()
  const quarter = quarterOf(today)
  const unused = unusedThisQuarter(state, today)
  const soonest = openItems(state, today).slice(0, 3)
  const attention = state.unmatched_credits.length + linkSuggestions(state).length

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle>Card Perks</CardTitle>
        <Button variant="ghost" size="sm" onClick={go}>
          View all <ChevronRight className="ml-1 h-4 w-4" />
        </Button>
      </CardHeader>
      <CardContent className="grid gap-6 pt-0 md:grid-cols-[220px_1fr]">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-gray-500">Unused this quarter</div>
          <div className="mt-1 text-3xl font-semibold tabular-nums text-gray-900 dark:text-gray-100">{money(unused.cents)}</div>
          <div className="mt-1 text-sm text-gray-500">
            {quarterName(quarter)} ends in {daysBetween(today, quarter.end)} days
          </div>
          {attention > 0 && (
            <button type="button" onClick={go} className="mt-3 text-sm font-medium text-primary-600 hover:underline">
              {plural(attention, 'item')} need{attention === 1 ? 's' : ''} a look
            </button>
          )}
        </div>
        <div className="-mx-3">
          {soonest.length === 0 && <p className="px-3 py-4 text-sm text-gray-500">Every credit is used for now.</p>}
          {soonest.map((item) => (
            <BenefitRow key={item.benefit.id} item={item} onOpen={go} />
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
